import { useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { RETURN_WINDOW_DAYS, VENDOR_RESPONSE_HOURS, VENDOR_FULFIL_DAYS } from "./ReturnsShared";

export default function ReturnRequestDialog({ orderId, item, onCreated }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("refund");
  const [reason, setReason] = useState("");
  const [photoBase64, setPhotoBase64] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Please attach a photo under 4MB"); return; }
    const reader = new FileReader();
    reader.onloadend = () => setPhotoBase64(reader.result || "");
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!reason.trim()) return toast.error("Tell us why you're returning this item");
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/returns`, {
        product_id: item.product_id, reason: reason.trim(), kind, photo_base64: photoBase64 || undefined,
      });
      toast.success("Request submitted. The exhibitor has 48 hours to respond.");
      setOpen(false); setReason(""); setPhotoBase64(""); setKind("refund");
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid={`return-open-${item.product_id}`}
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#F48E2E] hover:text-[#D97815]"
      >
        <RotateCcw className="h-3 w-3" /> Request return or replacement
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Return or Replacement</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex gap-3 items-center rounded-xl border border-gray-200 bg-gray-50 p-3">
              {item.image_base64 && <img src={item.image_base64} alt="" className="h-12 w-12 rounded-md object-cover" />}
              <div className="min-w-0">
                <div className="font-medium truncate">{item.name}</div>
                <div className="text-xs text-gray-500">Sold by {item.vendor_business_name || "Exhibitor"} · Qty {item.quantity}</div>
              </div>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 leading-relaxed">
              You can raise a request within {RETURN_WINDOW_DAYS} days of delivery. The exhibitor must accept or reject within {VENDOR_RESPONSE_HOURS} hours. On acceptance, replacement or pickup happens within {VENDOR_FULFIL_DAYS} days. Missed windows are auto-escalated to CityShor Support.
            </div>

            <div>
              <Label>What would you like?</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${kind === "refund" ? "border-[#F48E2E] bg-[#FEF3E6]" : "border-gray-200"}`}>
                  <input type="radio" name="kind" value="refund" checked={kind === "refund"} onChange={() => setKind("refund")} data-testid="return-kind-refund" className="accent-[#F48E2E]" />
                  <span className="text-sm">Refund</span>
                </label>
                <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${kind === "replacement" ? "border-[#F48E2E] bg-[#FEF3E6]" : "border-gray-200"}`}>
                  <input type="radio" name="kind" value="replacement" checked={kind === "replacement"} onChange={() => setKind("replacement")} data-testid="return-kind-replacement" className="accent-[#F48E2E]" />
                  <span className="text-sm">Replacement</span>
                </label>
              </div>
            </div>

            <div>
              <Label>Reason</Label>
              <Textarea
                data-testid="return-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Damaged in transit, wrong size delivered, colour mismatch, etc."
                rows={3}
              />
            </div>

            <div>
              <Label>Photo (optional)</Label>
              <input data-testid="return-photo" type="file" accept="image/*" onChange={onFile} className="text-xs" />
              {photoBase64 && <img src={photoBase64} alt="proof" className="mt-2 h-24 w-24 rounded-md object-cover border border-gray-200" />}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} className="rounded-full">Cancel</Button>
              <Button
                data-testid="return-submit"
                onClick={submit}
                disabled={busy}
                className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white"
              >
                {busy ? "Submitting…" : "Submit request"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
